import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ArcheHeader } from '../../../components/ArcheHeader';
import { useEnvelopes, useCreateEnvelope } from '../hooks';
import type { DocumentSignEnvelopeResponse, DocumentSignEnvelopeStatus } from '@/lib/api';

const STATUS_FILTERS: {
  key: string;
  label: string;
  statuses: DocumentSignEnvelopeStatus[];
}[] = [
  { key: 'all', label: 'All', statuses: [] },
  { key: 'drafts', label: 'Drafts', statuses: ['draft'] },
  { key: 'out', label: 'Out for signature', statuses: ['sent', 'in_progress'] },
  { key: 'completed', label: 'Completed', statuses: ['completed'] },
  { key: 'voided', label: 'Voided', statuses: ['voided'] },
  { key: 'declined', label: 'Declined', statuses: ['declined'] },
  { key: 'expired', label: 'Expired', statuses: ['expired'] },
];

const PAGE_SIZE = 20;

export function EnvelopesListPage() {
  const navigate = useNavigate();
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set(['all']));
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const statuses = activeFilters.has('all')
    ? undefined
    : Array.from(
        new Set(STATUS_FILTERS.filter((f) => activeFilters.has(f.key)).flatMap((f) => f.statuses)),
      );

  const { envelopes, total, loading, error } = useEnvelopes({
    status: statuses && statuses.length > 0 ? statuses : undefined,
    search: search || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });
  const createEnvelope = useCreateEnvelope();

  async function handleCreate() {
    const envelope = await createEnvelope.mutateAsync({ title: 'Untitled envelope' });
    navigate(`/a/arche_document_sign/envelope/${envelope.envelopeId}`);
  }

  function toggleFilter(key: string) {
    setPage(0);
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (key === 'all') {
        return new Set(['all']);
      }
      next.delete('all');
      if (next.has(key)) next.delete(key);
      else next.add(key);
      if (next.size === 0) next.add('all');
      return next;
    });
  }

  function applySearch(e: React.FormEvent) {
    e.preventDefault();
    setPage(0);
    setSearch(searchInput.trim());
  }

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <ArcheHeader archeId="arche_document_sign" title="Document Sign" />
        <div className="max-w-5xl mx-auto p-6 w-full">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min(total, (page + 1) * PAGE_SIZE);

  return (
    <div className="flex flex-col h-full">
      <ArcheHeader archeId="arche_document_sign" title="Document Sign">
        <Button onClick={handleCreate} disabled={createEnvelope.isPending}>
          <Plus className="h-4 w-4 mr-2" />
          New envelope
        </Button>
      </ArcheHeader>
      <div className="max-w-5xl mx-auto p-6 space-y-4 w-full">
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((f) => {
            const active = activeFilters.has(f.key);
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => toggleFilter(f.key)}
                aria-pressed={active}
                className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                  active
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-muted hover:bg-accent/40'
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        <form onSubmit={applySearch} className="flex gap-2" role="search">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by title or recipient email"
              className="pl-9"
              aria-label="Search envelopes"
              maxLength={200}
            />
          </div>
          <Button type="submit" variant="outline">
            Search
          </Button>
          {search && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setSearch('');
                setSearchInput('');
                setPage(0);
              }}
            >
              Clear
            </Button>
          )}
        </form>

        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-16 rounded-lg" />
            ))}
          </div>
        ) : envelopes.length === 0 ? (
          <EmptyState
            search={search}
            activeFilterKey={Array.from(activeFilters)[0] ?? 'all'}
            onCreate={handleCreate}
            disabled={createEnvelope.isPending}
          />
        ) : (
          <>
            <div className="border rounded-lg divide-y" role="list">
              {envelopes.map((env) => (
                <EnvelopeRow
                  key={env.envelopeId}
                  envelope={env}
                  onClick={() =>
                    navigate(
                      env.status === 'draft'
                        ? `/a/arche_document_sign/envelope/${env.envelopeId}`
                        : `/a/arche_document_sign/envelope/${env.envelopeId}/detail`,
                    )
                  }
                />
              ))}
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                Showing {showingFrom}–{showingTo} of {total}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  Previous
                </Button>
                <span className="self-center" aria-live="polite">
                  Page {page + 1} of {totalPages}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page + 1 >= totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function EmptyState({
  search,
  activeFilterKey,
  onCreate,
  disabled,
}: {
  search: string;
  activeFilterKey: string;
  onCreate: () => void;
  disabled: boolean;
}) {
  if (search) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <FileText className="h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-lg text-muted-foreground mb-1">No envelopes match "{search}"</p>
        <p className="text-sm text-muted-foreground">Try a different title or recipient email.</p>
      </div>
    );
  }
  if (activeFilterKey !== 'all') {
    const filter = STATUS_FILTERS.find((f) => f.key === activeFilterKey);
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <FileText className="h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-lg text-muted-foreground">
          No envelopes in {filter?.label.toLowerCase() ?? 'this state'}
        </p>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <FileText className="h-12 w-12 text-muted-foreground mb-4" />
      <p className="text-lg text-muted-foreground mb-4">No envelopes yet</p>
      <Button onClick={onCreate} disabled={disabled}>
        <Plus className="h-4 w-4 mr-2" />
        Create your first envelope
      </Button>
    </div>
  );
}

function EnvelopeRow({
  envelope,
  onClick,
}: {
  envelope: DocumentSignEnvelopeResponse;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="listitem"
      onClick={onClick}
      className="w-full text-left p-4 hover:bg-accent/40 flex items-center justify-between"
    >
      <div>
        <div className="font-medium">{envelope.title || 'Untitled envelope'}</div>
        <div className="text-xs text-muted-foreground mt-1">
          Updated {new Date(envelope.updatedAt).toLocaleString()}
        </div>
      </div>
      <span className="text-xs uppercase tracking-wide text-muted-foreground">
        {envelope.status.replace('_', ' ')}
      </span>
    </button>
  );
}
